import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, TestTube2, Users } from 'lucide-react';
import { AudienceFilters } from '../../components/AudienceFilters';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { useDebounced } from '../../lib/debounce';
import { formatNumber } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  AudienceFilter,
  Campaign,
  PushSendResult,
  Template,
} from '../../lib/types';

/** How long the audience count waits for typing to settle. */
const COUNT_DEBOUNCE_MS = 400;

/**
 * Send a message now.
 *
 * Copy arrives one of two ways, and both are first class. A **template** is
 * the reusable, translated path — use it for anything that will be sent more
 * than once. **Writing it here** is for the message that will only ever go out
 * once: an incident notice, a note to nine specific people. Forcing that
 * through a template would leave the template list full of single-use rows
 * nobody dares delete.
 */
export function ComposeTab({ onSent }: { onSent: () => void }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'template' | 'adhoc'>('adhoc');
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<AudienceFilter>({});
  const [namedIds, setNamedIds] = useState('');
  const [testDeviceId, setTestDeviceId] = useState('');
  const [confirming, setConfirming] = useState(false);

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/api/notifications/templates'),
  });

  // Naming devices explicitly is just a filter with `deviceIds` set — the
  // backend makes no distinction, so neither does this.
  const resolvedAudience = useMemo<AudienceFilter>(() => {
    const ids = namedIds
      .split(/[\s,]+/)
      .map((id) => id.trim())
      .filter(Boolean);
    return compact({
      ...audience,
      deviceIds: ids.length > 0 ? [...new Set(ids)] : undefined,
    });
  }, [audience, namedIds]);

  const debounced = useDebounced(resolvedAudience, COUNT_DEBOUNCE_MS);

  const count = useQuery({
    queryKey: ['audience-count', debounced],
    queryFn: () =>
      api.post<{ count: number }>(
        '/api/notifications/audience-count',
        debounced,
      ),
  });

  const payload = () => ({
    templateId: mode === 'template' ? templateId : undefined,
    title: mode === 'adhoc' ? title : undefined,
    body: mode === 'adhoc' ? body : undefined,
    audience: resolvedAudience,
  });

  const send = useMutation({
    mutationFn: () => api.post<Campaign>('/api/notifications/send', payload()),
    onSuccess: (campaign) => {
      toast.success(
        t('notif.compose.sent', {
          count: formatNumber(campaign.totalRecipients),
        }),
      );
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      onSent();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const sendTest = useMutation({
    mutationFn: () =>
      api.post<PushSendResult>('/api/notifications/test', {
        templateId: mode === 'template' ? templateId : undefined,
        title: mode === 'adhoc' ? title : undefined,
        body: mode === 'adhoc' ? body : undefined,
        deviceId: testDeviceId.trim(),
      }),
    onSuccess: (result) => {
      if (result.sent > 0) toast.success(t('notif.compose.testSent'));
      else if (result.skipped > 0) {
        toast.error(t('notif.compose.testSkipped'));
      } else toast.error(t('notif.compose.testRejected'));
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const template = templates.data?.find((item) => item.id === templateId);
  const hasCopy =
    mode === 'template' ? !!templateId : !!title.trim() && !!body.trim();
  const recipients = count.data?.count ?? 0;
  const ready = hasCopy && recipients > 0;

  return (
    <div className="space-y-4">
      <Step number={1} title={t('notif.compose.step1')}>
        <div className="space-y-3">
          <Field label={t('notif.compose.source')}>
            <Select
              value={mode}
              onChange={(event) =>
                setMode(event.target.value as 'template' | 'adhoc')
              }
            >
              <option value="adhoc">{t('notif.compose.sourceAdhoc')}</option>
              <option value="template">
                {t('notif.compose.sourceTemplate')}
              </option>
            </Select>
          </Field>

          {mode === 'adhoc' ? (
            <>
              <Field
                label={t('common.title')}
                hint={t('notif.compose.titleHint')}
              >
                <Input
                  value={title}
                  maxLength={120}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={t('notif.compose.titlePlaceholder')}
                />
              </Field>
              <Field
                label={t('common.body')}
                hint={t('notif.compose.bodyHint')}
              >
                <Textarea
                  rows={3}
                  maxLength={500}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder={t('notif.compose.bodyPlaceholder')}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label={t('common.template')}>
                <Select
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                >
                  <option value="">{t('common.selectTemplate')}</option>
                  {templates.data?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Field>

              {template && <TemplatePreview template={template} />}
            </>
          )}
        </div>
      </Step>

      <Step number={2} title={t('notif.compose.step2')}>
        <div className="space-y-3">
          <Field
            label={t('notif.compose.namedUsers')}
            hint={t('notif.compose.namedUsersHint')}
          >
            <Textarea
              rows={2}
              className="font-mono text-xs"
              value={namedIds}
              onChange={(event) => setNamedIds(event.target.value)}
              placeholder="a1b2c3…, d4e5f6…"
            />
          </Field>

          <AudienceFilters value={audience} onChange={setAudience} />

          <div className="flex items-center gap-2 rounded-lg border border-line bg-surface px-3.5 py-3">
            <Users size={16} className="text-ink-3" />
            {count.isPending || count.isFetching ? (
              <Spinner className="size-4" />
            ) : count.isError ? (
              <span className="text-sm text-danger">
                {errorMessage(count.error)}
              </span>
            ) : (
              <span className="text-sm text-ink">
                <span className="tnum">
                  {t('notif.compose.willReceive', {
                    count: formatNumber(recipients),
                  })}
                </span>
                <span className="ml-1 text-ink-3">
                  {t('notif.compose.silencedSkipped')}
                </span>
              </span>
            )}
          </div>
        </div>
      </Step>

      <Step number={3} title={t('notif.compose.step3')}>
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <Field
              label={t('notif.compose.testOn')}
              hint={t('notif.compose.testHint')}
              className="min-w-[220px] flex-1"
            >
              <Input
                className="font-mono text-xs"
                value={testDeviceId}
                onChange={(event) => setTestDeviceId(event.target.value)}
                placeholder={t('common.pasteDeviceId')}
              />
            </Field>
            <Button
              icon={<TestTube2 size={16} />}
              disabled={!hasCopy || !testDeviceId.trim()}
              loading={sendTest.isPending}
              onClick={() => sendTest.mutate()}
            >
              {t('notif.compose.sendTest')}
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
            <p className="text-xs text-ink-3">
              {t('notif.compose.backgroundNote')}
            </p>
            <Button
              variant="primary"
              icon={<Send size={16} />}
              disabled={!ready}
              onClick={() => setConfirming(true)}
            >
              {t('notif.compose.send')}
            </Button>
          </div>
        </div>
      </Step>

      <ConfirmDialog
        open={confirming}
        title={t('notif.compose.confirmTitle', {
          count: formatNumber(recipients),
        })}
        description={t('notif.compose.confirmDescription')}
        confirmLabel={t('notif.compose.confirmAction')}
        loading={send.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => send.mutate()}
      />
    </div>
  );
}

function TemplatePreview({ template }: { template: Template }) {
  const t = useT();
  const english =
    template.locales.find((locale) => locale.language === 'en') ??
    template.locales[0];

  return (
    <div className="rounded-lg border border-line bg-raised px-3 py-2.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <Badge tone="brand">
          {t('notif.compose.languages', { count: template.locales.length })}
        </Badge>
        <span className="font-mono text-xs text-ink-3">{template.key}</span>
      </div>
      <p className="text-sm font-medium text-ink">{english?.title}</p>
      <p className="mt-0.5 text-xs text-ink-2">{english?.body}</p>
      <p className="mt-2 text-xs text-ink-3">
        {t('notif.compose.previewNote')}
        {template.placeholders.length > 0 &&
          ` ${t('notif.compose.previewPlaceholders', {
            list: template.placeholders.join(', '),
          })}`}
      </p>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        title={title}
        action={
          <span className="grid h-6 w-6 place-items-center rounded-full bg-raised text-xs font-semibold text-ink-2">
            {number}
          </span>
        }
      />
      <div className="p-4 sm:p-5">{children}</div>
    </Card>
  );
}
